import { reactive, computed } from "vue";

/**
 * Composable for managing modal state in booking components
 * Extracts modal state management from components
 */
export function useModalState() {
	const state = reactive({
		show: false,
		mode: "create", // 'create' | 'edit' | 'view' | 'edit-series' | 'edit-instance' | 'conflict'
		booking: null,
		selectedSlot: null,
		prefillData: null,
		isDragDrop: false,
		isWalkIn: false,
	});

	const isCreateMode = computed(() => state.mode === "create");
	const isEditMode = computed(() => state.mode === "edit");
	const isViewMode = computed(() => state.mode === "view");
	const isEditSeriesMode = computed(() => state.mode === "edit-series");
	const isEditInstanceMode = computed(() => state.mode === "edit-instance");
	const isConflictMode = computed(() => state.mode === "conflict");
	const isEditingMode = computed(() => 
		isEditMode.value || isEditSeriesMode.value || isEditInstanceMode.value
	);

	const showModal = (mode, options = {}) => {
		state.mode = mode;
		state.booking = options.booking || null;
		state.selectedSlot = options.selectedSlot || null;
		state.prefillData = options.prefillData || null;
		state.isDragDrop = options.isDragDrop || false;
		state.isWalkIn = options.isWalkIn || false;
		state.show = true;
	};

	const closeModal = () => {
		state.show = false;
		state.booking = null;
		state.selectedSlot = null;
		state.prefillData = null;
		state.isDragDrop = false;
		state.isWalkIn = false;
		state.mode = "create";
	};

	/**
	 * Return to the create form, e.g. after backing out of a conflict/budget/compliance step.
	 *
	 * @param {object|null} prefillData
	 * @param {{ keepWalkIn?: boolean }} [options] keepWalkIn keeps the walk-in ("Sofortbuchung")
	 *   form the user started in - without it they would silently land in the regular booking
	 *   form. The selected slot is kept as well, since the walk-in form reads the room from it.
	 */
	const switchToCreateMode = (prefillData = null, options = {}) => {
		const keepWalkIn = Boolean(options.keepWalkIn);

		state.prefillData = prefillData;
		state.mode = "create";
		state.booking = null;
		state.selectedSlot = keepWalkIn ? state.selectedSlot : null;
		state.isDragDrop = false;
		state.isWalkIn = keepWalkIn;
		state.show = true;
	};

	const switchToEditMode = (booking) => {
		state.mode = "edit";
		state.booking = booking;
		state.selectedSlot = null;
		state.prefillData = null;
		state.isDragDrop = false;
	};

	const switchToEditSeriesMode = (booking, prefillData = null, isDragDrop = false) => {
		state.mode = "edit-series";
		state.booking = booking;
		state.selectedSlot = null;
		state.prefillData = prefillData;
		state.isDragDrop = isDragDrop;
		state.show = true;
	};

	const switchToEditInstanceMode = (booking, prefillData = null, isDragDrop = false) => {
		state.mode = "edit-instance";
		state.booking = booking;
		state.selectedSlot = null;
		state.prefillData = prefillData;
		state.isDragDrop = isDragDrop;
		state.show = true;
	};

	const switchToConflictMode = () => {
		state.mode = "conflict";
		state.show = true;
	};

	const openCreateModal = (selectedSlot, isWalkIn = false) => {
		showModal("create", { selectedSlot, isWalkIn });
	};

	const openViewModal = (booking) => {
		showModal("view", { booking });
	};

	const openEditModal = (booking, prefillData = null, isDragDrop = false) => {
		showModal("edit", { booking, prefillData, isDragDrop });
	};

	return {
		// State
		modalState: state,
		
		// Computed
		isCreateMode,
		isEditMode,
		isViewMode,
		isEditSeriesMode,
		isEditInstanceMode,
		isConflictMode,
		isEditingMode,

		// Actions
		showModal,
		closeModal,
		switchToCreateMode,
		switchToEditMode,
		switchToEditSeriesMode,
		switchToEditInstanceMode,
		switchToConflictMode,
		openCreateModal,
		openViewModal,
		openEditModal,
	};
}